<script setup lang="ts">
import {AxiosError, AxiosResponse} from 'axios';
import {useField, useForm} from 'vee-validate';
import {inject, ref} from 'vue';
import {useRouter} from 'vue-router';
import * as yup from 'yup'
import {useUser} from './stores/userStore';
import ThemeToggleFab from './components/ThemeToggleFab.vue';
import BrandedLogo from './components/BrandedLogo.vue';
import {Buffer} from 'buffer';
import {ThemeInstance, useTheme} from 'vuetify';
import {PortalAuthKey} from "./plugins/auth.ts";
import AuthLayout from "./layouts/AuthLayout.vue";
import api from "./api.ts";

const theme: ThemeInstance = useTheme();

const auth = inject(PortalAuthKey);

const router = useRouter()

interface IFormData {
  current_password: string;
  new_password: string;
  confirm_new_password: string
}

const schema = yup.object({
  current_password: yup.string().required("Current password is required"),
  new_password: yup.string().required("New password is required").matches(
      /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
      "Password must contain at least 8 characters, one uppercase, one number and one special case character"
  ),
  confirm_new_password: yup.string().required("New password is required").oneOf([yup.ref('new_password'), null], "Passwords must match")
})

const {handleSubmit, resetForm} = useForm({
  validationSchema: schema,
})

const current_password = useField('current_password')
const new_password = useField('new_password')
const confirm_new_password = useField('confirm_new_password')

const onSubmit = handleSubmit((values: IFormData) => {
  changePassword(values)
})

const loading = ref<boolean>()
const success = ref<boolean>()
const errorAlert = ref<boolean>()
const errorMsg = ref<string>()

const changePassword = (values: IFormData) => {
  const credentials: string = Buffer.from(`${values.current_password}:${values.confirm_new_password}`, "ascii").toString("base64");
  loading.value = true
  const data = {
    // current_pwd: values.current_password,
    // new_pwd: values.confirm_new_password,
    user_id: auth.authState.user.id,
    credentials
  }
  api.post(`/api/auth/password/change`, data, {
    withCredentials: true,
    headers: {
      Authorization: "Bearer " + document.cookie
          .split("; ")
          .find((row) => row.startsWith("access_token="))
          ?.split("=")[1]
    }
  }).then(async (response: AxiosResponse) => {
    loading.value = false
    if (response.status === 200) {
      success.value = true
      await new Promise(r => setTimeout(r, 2000)).then(() => {
        api.post(`/api/auth/logout`, {user_id: auth.authState.user.id}, {
          withCredentials: true,
          headers: {
            Authorization: "Bearer " + document.cookie
                .split("; ")
                .find((row) => row.startsWith("access_token="))
                ?.split("=")[1]
          }
        }).then((response: AxiosResponse) => {
          router.push({name: 'app.login'})
        }).catch((error: AxiosError) => {
          loading.value = false
          errorAlert.value = true
          //@ts-ignore
          errorMsg.value = error.response.data.msg
        })
      })
    } else {
      loading.value = false
      errorAlert.value = true
      errorMsg.value = response.data.msg
    }
  }).catch((error: AxiosError) => {
    loading.value = false
    errorAlert.value = true
    //@ts-ignore
    errorMsg.value = error.response.data.msg
  })
}

</script>

<template>
  <!--    <div class="main d-flex align-center justify-center bg-primary"-->
  <!--        :class="theme.global.name.value === 'darkTheme' ? 'genauth-background-dark' : 'genauth-background'">-->
  <AuthLayout>
    <!-- <div class="main d-flex align-center justify-center bg-primary"
    style="height: 100vh; background: linear-gradient(to bottom right, #202945, #0071ce)"> -->
    <v-sheet v-if="!loading && !success" width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8">
      <!-- <v-img src="https://oiforroi.com/wp-content/uploads/2021/09/logo-oi.svg" /> -->
      <BrandedLogo></BrandedLogo>
      <br>
      <!-- <v-alert v-if="errorAlert" type="error" text="There was an error chaning your password."></v-alert> -->
      <v-alert v-if="errorAlert" color="error">
        <p>
          We could not reset your password for the following reason:
        </p>
        <br>
        <strong>{{ errorMsg }}</strong>
      </v-alert>
      <br>
      <v-alert color="info" text="Enter your current password and then choose a new one"></v-alert>
      <br>
      <v-text-field autofocus class="my-2" label="Enter Current Password" type="password"
                    v-model="current_password.value.value"
                    :error-messages="current_password.errorMessage.value"></v-text-field>

      <v-text-field class="my-2" label="Enter New Password" type="password" v-model="new_password.value.value"
                    :error-messages="new_password.errorMessage.value"></v-text-field>

      <v-text-field class="my-2" label="Confirm New Password" type="password"
                    v-model="confirm_new_password.value.value"
                    :error-messages="confirm_new_password.errorMessage.value"></v-text-field>

      <v-btn color="primary" block class="mt-2" size="large" @click="onSubmit">
        <span class="mx-2">Submit</span>
      </v-btn>
      <v-btn color="primary" block class="mt-2" variant="text" to="/account">Cancel</v-btn>
    </v-sheet>
    <v-sheet width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8" v-else-if="loading">
      <v-progress-circular :size="100" :width="8" color="primary" indeterminate></v-progress-circular>
    </v-sheet>
    <v-sheet width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8"
             v-else-if="success && !loading">
      <v-icon class="mb-5" color="success" icon="mdi-check-circle" size="112"></v-icon>
      <h2 class="text-h5 mb-6">Password Change Successful!</h2>
      <p class="mb-4 text-medium-emphasis text-body-2">You will be redirected to login momentarily.
      </p>
    </v-sheet>
    <!--    </div>-->
    <!--    <ThemeToggleFab></ThemeToggleFab>-->
  </AuthLayout>
</template>